import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { ActionsSubject } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { NativeDateAdapter } from "@angular/material/core";
import { SelectionModel } from "@angular/cdk/collections";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { TranslocoService } from "@ngneat/transloco";
import { faChartSimple, faFilter } from "@fortawesome/free-solid-svg-icons";
import { timeout } from "../../../decorators/timeout";
import { SpecialCompbaser } from "../../../components/compbaser";
import { DialogPopComponent } from "../../../components/dialog-pop/dialog-pop.component";
import { LocalStoreService, StoreSelectorsService, UserService } from "../../../services";
import { IPlayerProps, IPlayerStatistic, IResource, IStationData } from "../../../interfaces";
import { timer } from "rxjs"; // Correct: Import timer from 'rxjs'
import { takeUntil, tap } from "rxjs/operators"; // Correct: Import operators from 'rxjs/operators'
import { actionLoadStationStatistic, actionLoadStationStatisticReply } from "../../../store/actions/user.actions";
import {
    compareIds,
    convertBooleanToNumbers,
    convertNumberToBoolean,
    isFalsy,
    LargePageSizeOptions,
} from "../../../common";
import * as _ from "lodash";

@Component({
    selector: "app-station-statistic",
    templateUrl: "./station-statistic.component.html",
    styleUrls: ["./station-statistic.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [NativeDateAdapter],
})
export class StationStatisticComponent extends SpecialCompbaser implements OnInit {
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    readonly faFilter = faFilter;
    readonly faChartSimple = faChartSimple;
    readonly pageSizeOptions = LargePageSizeOptions;
    readonly whiteColor = "#FFFFFF";

    loading = true;
    scenes: IPlayerProps[] = [];
    resources: IResource[] = [];
    stations: IStationData[] = [];
    pageSize = 10;
    dataSource: MatTableDataSource<IPlayerStatistic>;
    dataColumns: string[] = ["date", "hour", "stationId", "station", "tag", "category", "content", "label", "count", "duration", "select"];
    mainFields: string[] = ["stationId", "year", "month", "day", "hour", "tag", "contentType", "contentId"];
    displayedColumns: string[] = [];
    aggregateOptions = ["date", "hour", "stationId", "tag", "category", "content"];
    aggragateFieldMapping = {
        date: "date",
        hour: "hours",
        stationId: "stations",
        category: "categories",
        content: "contents",
        tag: "tags",
    };
    aggregateSelection = [];
    aggregateDatetimeOptions = ["date", "hour"];
    filteredStatisticData: IPlayerStatistic[] = [];
    originalStatisticData: IPlayerStatistic[] = [];
    dateFilter = "thismonth";
    chartType = "pie";
    prevChartType = "pie";
    chartEnabled = true;
    isShowChart = false;
    filters = {};
    selection: SelectionModel<string>;
    range = new FormGroup({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null),
    });
    colors = {};
    showSelectedOnly = false;
    chartData;
    lastUpdate = "";

    constructor(
        private storeSelector: StoreSelectorsService,
        private actionsSubj: ActionsSubject,
        private localStoreService: LocalStoreService,
        private translocoService: TranslocoService,
        private userService: UserService,
        private toastr: ToastrService,
        private cd: ChangeDetectorRef,
        protected override dialog: MatDialog,
    ) {
        super(dialog);
        this.displayedColumns = this.dataColumns;
        this.aggregateSelection = _.clone(this.aggregateOptions);

        this.cod(
            this.actionsSubj.pipe(
                ofType(actionLoadStationStatisticReply),
                takeUntil(timer(20000).pipe(
                    tap(() => {
                        this.loading = false; // Stop spinner if no reply after 20 seconds
                        // this.toastr.warning(this.translocoService.translate("toast.dataLoadTimeout", {}, this.translocoService.getActiveLang())); // Optional: notify user
                        this.cd.detectChanges();
                    }),
                )),
            ).subscribe((reply: any) => {
                if (reply) {
                    this.createTable(reply?.data);
                    this.lastUpdate = reply?.lastUpdate;
                }
                this.loading = false; // Stop spinner on successful reply
            }),
        );

        this.selection = new SelectionModel<string>(true, []);
        this.chartEnabled = convertNumberToBoolean(this.localStoreService.getStorageValConfig("playersStatsChartEnabled"));
    }

    ngOnInit() {
        this.initializeDataSource(); // Ensure dataSource is initialized early
        this.cod(
            this.storeSelector.getResources(false).subscribe((resources: IResource[]) => {
                this.resources = resources;
            }),
        );

        this.cod(
            this.storeSelector.getScenes(null, false).subscribe((i_scenes) => {
                this.scenes = i_scenes;
            }),
        );

        this.cod(
            this.storeSelector.getStationsComplete().subscribe((v) => {
                if (!v?.length) return;
                this.stations = v;
            }),
        );

        this.storeSelector.dispatch(
            actionLoadStationStatistic({
                payload: {},
            }),
        );
    }

    @timeout(333)
    createTable(data) {
        const rows: IPlayerStatistic[] = [];
        if (data) {
            Object.keys(data).forEach((key) => {
                const values = key.split(".");
                if (values.length < 8) return;

                const row: IPlayerStatistic = {
                    stationId: values[0]?.trim() || "",
                    year: values[1]?.trim() || "",
                    month: values[2]?.trim() || "",
                    day: values[3]?.trim() || "",
                    hour: values[4]?.trim() || "",
                    tag: values[5]?.trim() || "",
                    contentType: values[6]?.trim(),
                    contentId: values[7]?.trim() || "",
                    count: +parseFloat(data[key].count || "0").toFixed(2),
                    duration: +parseFloat(data[key].duration || "0").toFixed(2),
                    uuid: key,
                };

                if (row.contentType == "0") {
                    row.content = `resource.${row.contentId}`;
                    const resource = this.resources.find((r) => compareIds(r.resource_id, row.contentId));
                    if (resource) {
                        row.label = resource.resource_name;
                        row.category = resource.tree_path;
                    }
                } else if (row.contentType == "1") {
                    row.content = `scene.${row.contentId}`;
                    const scene = this.scenes.find((s) => compareIds(s.Player.$.id, row.contentId));
                    if (scene) {
                        row.label = scene.Player.$.label;
                        row.category = scene.Player.$.folder;
                    }
                } else {
                    row.content = `key.${row.contentId}`;
                    row.label = row.contentId;
                    row.category = "";
                }

                if (row.stationId) {
                    const st = this.stations.find((s) => compareIds(s.props.station_id, row.stationId));
                    if (st && st.props.station_name) {
                        row.station = `${st.props.station_name}`;
                    } else {
                        row.station = "";
                    }
                }
                row.date = `${row.year}-${("0" + row.month).slice(-2)}-${("0" + row.day).slice(-2)}`;
                rows.push(row);
            });
        }

        if (this.originalStatisticData.length > 0) {
            rows.forEach((row) => {
                const existingRow = this.originalStatisticData.find((row1) => this.mainFields.every((field) => row1[field] == row[field]));
                if (existingRow) {
                    existingRow.count = row.count;
                    existingRow.duration = row.duration;
                } else {
                    this.originalStatisticData.push(row);
                }
            });
            this.filteredStatisticData = this.originalStatisticData;
        } else {
            this.filteredStatisticData = rows;
            this.originalStatisticData = rows;
        }

        if (!this.dataSource) {
            this.initializeDataSource();
        }

        this.filterByDate();
    }

    initializeDataSource() {
        this.dataSource = new MatTableDataSource();
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.dataSource.filterPredicate = this.customFilterPredicate();
        // this.dataSource.filterPredicate = (data: any, filter) => {
        //     const dataStr = JSON.stringify(data).toLowerCase();
        //     return dataStr.indexOf(filter) !== -1;
        // };
        this.dataSource.sortingDataAccessor = (item: IPlayerStatistic, property) => {
            switch (property) {
                case "count":
                case "duration":
                case "stationId":
                case "hour":
                    return +item[property];
                default: {
                    return item[property];
                }
            }
        };
    }

    filterByDate() {
        const currentDate = new Date();
        let checkDay = false;

        switch (this.dateFilter) {
            case "": {
                this.filteredStatisticData = this.originalStatisticData;
                this.dataSource.data = this.filteredStatisticData;
                return;
            }
            case "month1": {
                currentDate.setMonth(currentDate.getMonth() - 1);
                break;
            }
            case "month2": {
                currentDate.setMonth(currentDate.getMonth() - 2);
                break;
            }
            case "today": {
                checkDay = true;
                break;
            }
            case "yesterday": {
                currentDate.setDate(currentDate.getDate() - 1);
                checkDay = true;
                break;
            }
            case "thisweek": {
                const startDate = this.startOfWeek(new Date());
                this.range.setValue({
                    start: startDate,
                    end: currentDate,
                });
                this.filterByDateRange();
                return;
            }
            case "lastweek": {
                const startDate = this.startOfWeek(new Date());
                this.range.setValue({
                    start: new Date(startDate.setDate(startDate.getDate() - 7)),
                    end: new Date(startDate.setDate(startDate.getDate() + 6)),
                });
                this.filterByDateRange();
                return;
            }
            case "custom": {
                this.filterByDateRange();
                return;
            }
            case "thismonth": {
                break;
            }
        }

        const yy = `${currentDate.getFullYear()}`;
        const mm = `${currentDate.getMonth() + 1}`;
        const dd = `${currentDate.getDate()}`;

        this.filteredStatisticData = this.originalStatisticData.filter((row) => row.year == yy && row.month == mm && (!checkDay || row.day == dd));
        this.aggregateBy();
    }

    filterByDateRange() {
        const startDate = this.range.value.start;
        const endDate = this.range.value.end;
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 0, 0, 0);

        if (!startDate || !endDate) return;
        this.filteredStatisticData = this.originalStatisticData.filter((row) => {
            const dt = new Date(+row.year, +row.month - 1, +row.day);
            return dt.getTime() >= startDate.getTime() && dt.getTime() <= endDate.getTime();
        });
        this.aggregateBy();
    }

    aggregateBy(checked = false, value: string | null = null) {
        if (value) {
            const ind = this.aggregateSelection.indexOf(value);
            if (checked && ind === -1) { // Only push if checked and not already present
                this.aggregateSelection.push(value);
            } else if (!checked && ind !== -1) { // Only splice if unchecked and present
                this.aggregateSelection.splice(ind, 1);
            }
        }
        this.resetChartSelection();

        // if all selected, show all data
        if (this.aggregateOptions.length === this.aggregateSelection.length) {
            this.dataSource.data = this.filteredStatisticData;
            this.displayedColumns = this.dataColumns;
            return;
        }

        this.loading = true;
        this.cd.detectChanges();

        // set table display columns
        this.displayedColumns = this.dataColumns.filter((v) => !(this.aggregateOptions.includes(v) && !this.aggregateSelection.includes(v)));

        if (!this.aggregateSelection.includes("content")) {
            this.displayedColumns = this.displayedColumns.filter((v) => v !== "content" && v !== "label");
        }

        if (!this.aggregateSelection.includes("stationId")) {
            this.displayedColumns = this.displayedColumns.filter((v) => v !== "station" && v !== "stationId");
        }

        this.doAggregation();
    }

    private doAggregation() {
        const newData = {};
        _.cloneDeep(this.filteredStatisticData).forEach((row: IPlayerStatistic) => {
            let key = this.aggregateSelection.map((field) => row[field]).join(".");
            if (!key) {
                key = "empty";
            }

            if (!newData[key]) {
                newData[key] = _.clone(row);
                newData[key].uuid = key;
            } else {
                newData[key].count = +(newData[key].count + row.count).toFixed(2);
                newData[key].duration = +(newData[key].duration + row.duration).toFixed(2);
            }
        });

        this.dataSource.data = Object.values(newData);
        this.loading = false;
    }

    refresh() {
        this.loading = true;
        this.cd.detectChanges();
        this.storeSelector.dispatch(
            actionLoadStationStatistic({
                payload: {
                    lastUpdate: this.lastUpdate,
                },
            }),
        );
    }

    resetPOP() {
        this.aggregateSelection = _.clone(this.aggregateOptions);
        this.displayedColumns = this.dataColumns;
        this.dateFilter = "thismonth";
        this.filters = {};
        this.dataSource.data = [];
        this.resetChartSelection();
    }

    exportToCSV() {
        this.downloadFile(this.dataSource.data);
    }

    applyFilter() {
        if (this.dataSource) {
            this.dataSource.filter = JSON.stringify(this.filters);
        }
    }

    clearFilter(field = null) {
        if (!field) {
            this.filters = {};
        } else {
            this.filters[field] = "";
        }
        this.applyFilter();
    }

    colorPickerChange(event, row) {
        const color = event.target.value;
        if (!color) return;
        this.colors[row.uuid] = color;
        if (this.isShowChart) {
            this.drawChart();
        }
    }

    enableChart() {
        const val = convertBooleanToNumbers(this.chartEnabled, true);
        this.localStoreService.setStorageValConfig("playersStatsChartEnabled", val);
        this.resetChartSelection();
    }

    checkChartRow(row) {
        this.selection.toggle(row.uuid);
        if (this.isShowChart) {
            this.drawChart();
        }
    }

    changeChartType() {
        if (this.prevChartType === "pie" && this.chartType !== "pie") {
            this.aggregateSelection = this.aggregateSelection.filter((v) => !this.aggregateDatetimeOptions.includes(v));
            this.aggregateBy();
        }
        if (this.prevChartType === "pie" || this.chartType === "pie") {
            this.resetChartSelection();
        } else {
            this.drawChart();
        }
        this.prevChartType = this.chartType;
    }

    drawChart() {
        this.isShowChart = true;
        if (!this.enableChart || !this.selection.hasValue()) {
            return;
        }

        const chartData = _.cloneDeep(this.dataSource.data.filter((v) => this.selection.isSelected(v.uuid)));
        if (chartData.length === 0) return;

        if (this.chartType !== "pie") {
            chartData.forEach((row1) => {
                // Get the rows with the same aggregation fields
                const group = this.filteredStatisticData.filter((row2) => this.aggregateSelection.every((field) => row1[field] == row2[field]));

                // Aggregate count/duration by some fields except datetime
                if (this.aggregateSelection.length + this.aggregateDatetimeOptions.length == this.aggregateOptions.length) {
                    row1.group = group;
                    return;
                }
                const rows = _.cloneDeep(group).reduce((acc: IPlayerStatistic[], val: IPlayerStatistic) => {
                    const element = acc.find((row3) => this.aggregateDatetimeOptions.every((field) => row3[field] === val[field]));

                    if (!element) {
                        acc.push(val);
                    } else {
                        element.count = +(element.count + val.count).toFixed(2);
                        element.duration = +(element.duration + val.duration).toFixed(2);
                    }
                    return acc;
                }, []);
                row1.group = rows;
            });
        }

        this.chartData = {
            chartType: this.chartType,
            data: chartData,
            colors: this.colors,
            fields: this.aggregateSelection,
        };
    }

    changeShowSelectedOnly() {
        if (this.showSelectedOnly && this.selection.selected.length > 0) {
            this.filters["uuid"] = this.selection.selected;
        } else {
            this.filters["uuid"] = "";
        }
        if (this.dataSource) {
            this.dataSource.filter = JSON.stringify(this.filters);
        }
    }

    clearStatistic() {
        const localRemText1 = this.translocoService.translate("general.areYouSure", {}, this.translocoService.getActiveLang());
        const dialogRef = this.dialog.open(DialogPopComponent, {
            width: "400px",
            data: { name: `${localRemText1} player statistic?`, showCancel: true },
        });
        dialogRef.afterClosed().subscribe((r) => {
            if (r) {
                this.loading = true;
                this.cd.detectChanges();
                this.cod(
                    this.userService.clearStationStatistic().subscribe((data) => {
                        this.loading = false;
                        this.cd.detectChanges();

                        if (!data) {
                            this.toastr.warning(this.translocoService.translate("toast.problemNetwork", {}, this.translocoService.getActiveLang()));
                            return;
                        }

                        if (!data.status || isFalsy(data.status)) {
                            this.toastr.warning(data.error || "sorry, failed to clear statistic data.");
                        } else {
                            this.resetPOP();
                        }
                    }),
                );
            }
        });
    }

    private resetChartSelection() {
        this.selection.clear();
        this.colors = {};
        this.isShowChart = false;
        this.showSelectedOnly = false;
        this.changeShowSelectedOnly();
    }

    private downloadFile(data: any) {
        const replacer = (key, value) => (value === null ? "" : value); // specify how you want to handle null values here
        if (data.length === 0) return;
        const header = Object.keys(data[0]);
        const csv = data.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(","));
        csv.unshift(header.join(","));
        const csvArray = csv.join("\r\n");

        const a = document.createElement("a");
        const blob = new Blob([csvArray], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);

        a.href = url;
        a.download = "player-statistic.csv";
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
    }

    private customFilterPredicate() {
        const myFilterPredicate = (data: any, filter: string): boolean => {
            const filterObj = JSON.parse(filter);
            let isFilter = true;
            // tslint:disable-next-line
            for (const col in filterObj) {
                let colData = data[col];
                if (col === "uuid") {
                    if (filterObj[col]?.length && !filterObj[col].includes(colData)) {
                        isFilter = false;
                        break;
                    }
                } else {
                    if (col === "stationId") {
                        colData = `${colData}.${data["station"]}`;
                    }
                    if (typeof colData !== "undefined" && colData.toString().toLowerCase().indexOf(filterObj[col].trim().toLowerCase()) == -1) {
                        isFilter = false;
                        break;
                    }
                }
            }
            return isFilter;
        };
        return myFilterPredicate;
    }

    private startOfWeek(dt: Date) {
        const diff = dt.getDate() - dt.getDay() + (dt.getDay() === 0 ? -6 : 1);
        return new Date(dt.setDate(diff));
    }
}
